/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Group, Modal, Stack } from '@mantine/core';
import useModal from 'src/new_arch/hooks/useModal';
import PricingPlanPrice from 'src/entities/pricing/pricingPlanPrice';
import ReactLoader from 'react-loaders';

import PricingSubscriptionPresenter from '../../../../../presenters/PlanSettings/PricingSubscriptionPresenter';
import PlanCard from './PlanCard/PlanCard';
import PriceSwitch from './PriceSwitch/PriceSwitch';
import BillingPeriodTitle from './BillingPeriodTitle/BillingPeriodTitle';
import Loader from '../../../../../components/Loader/Loader';
import PricingPlan from '../../../../../entities/pricing/pricingPlan';
import { getPricingPlans } from '../../../../../selectors/pricingPlans';
import { getWorkspace } from '../../../../../selectors/workspaces';
import { connectComponent } from '../../../../../lib/connectComponent';

import './PricingSubscriptionSelectionModal.scss';
import CustomCard from './CustomCard/CustomCard';
import { PRICING_PLANS_AVAILABLE } from '../../../../../config/global';

export type PricingSubscriptionSelectionModalProps = {
	presenter: PricingSubscriptionPresenter;
	stackOrder: number;
	onClose: () => void;
	isLoadingPrices: boolean;
	fetchPricingPlans: () => void;
	onUpgradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void;
	onDowngradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void;
	onCancelDowngradeButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void;
	onNoWorkspaceButtonClicked: ( pricingPlanPrice: PricingPlanPrice ) => void;
	pricingPlans: PricingPlan[]
	isCreatingWorkspace: boolean;
	selectPlanMode?: boolean;
};

const MODAL_STYLES = {
	header: {
		padding: '12px 16px',
		borderBottom: '1px solid var(--mantine-color-gray-2)'
	},
	body: {
		padding: '24px'
	}
} as const;

const CreatingWorkspaceLoader = () => (
	<Stack
		w="100%"
		h="100%"
		bg="rgba(0,0,0,0.05)"
		align="center"
		justify="center"
		style={{ position: 'absolute', top: 0, left: 0 }}
	>
		<ReactLoader active type="ball-triangle-path" />
	</Stack>
);

export const PricingSubscriptionSelectionModal = ( {
	presenter, stackOrder = 1, isLoadingPrices = true, pricingPlans = [],
	onClose = () => { }, fetchPricingPlans = () => { },
	onUpgradeButtonClicked = () => { }, onDowngradeButtonClicked = () => { },
	onCancelDowngradeButtonClicked = () => { }, onNoWorkspaceButtonClicked = () => { },
	isCreatingWorkspace = false, selectPlanMode = false
}: PricingSubscriptionSelectionModalProps ) => {
	useEffect( () => { fetchPricingPlans(); }, [] );

	const {
		opened,
		modalStyles
	} = useModal( {
		name: 'subscription-selection',
		stackOrder,
		onClose,
		defaultStyles: MODAL_STYLES
	} );

	const renderPlanCard = ( pricingPlan: PricingPlan ) => (
		<PlanCard
			key={pricingPlan.id}
			presenter={presenter.planCardPresenterForPricingPlan( pricingPlan )}
			onUpgradeButtonClicked={onUpgradeButtonClicked}
			onDowngradeButtonClicked={onDowngradeButtonClicked}
			onCancelDowngradeButtonClicked={onCancelDowngradeButtonClicked}
			onNoWorkspaceButtonClicked={onNoWorkspaceButtonClicked}
			selectPlanMode={selectPlanMode}
		/>
	);

	const renderBillingPeriodSwitch = () => (
		<PriceSwitch
			isMonthly={presenter.isMonthly}
			annualDiscountPercentage={presenter.minimumAnualDiscountPercentage( pricingPlans )}
			onSwitch={presenter.onSwitched}
		/>
	);

	const renderBillingPeriodHeader = () => (
		presenter.shouldShowBillingPeriodSwitch
			? renderBillingPeriodSwitch()
			: <BillingPeriodTitle text={presenter.billingPeriodName} />
	);

	if ( !PRICING_PLANS_AVAILABLE ) { return null; }

	return (
		<Modal
			onClose={onClose}
			opened={opened}
			title="Change Workspace Plan"
			styles={modalStyles}
			size="1164px"
		>
			<Stack className="PricingSubscriptionSelectionModal" align="center" spacing="20px" data-testid="pricing-subscription-selection-modal">
				{isLoadingPrices
					? <Loader active />
					: (
						<>
							{renderBillingPeriodHeader()}
							<Group w="100%" spacing="35px" align="stretch" grow noWrap>
								{pricingPlans.map( renderPlanCard )}
								<CustomCard />
							</Group>
							{isCreatingWorkspace && <CreatingWorkspaceLoader />}
						</>
					)}
			</Stack>
		</Modal>
	);
};

export const PricingSubscriptionSelectionModalWithObserver = observer(
	// @ts-ignore
	PricingSubscriptionSelectionModal
);

export default connectComponent( ( state: Record<string, unknown>, props: Record<string, unknown> ) => ( {
	// @ts-expect-error
	workspace: getWorkspace( state, props ),
	// @ts-expect-error
	pricingPlans: getPricingPlans( state, props ).valueSeq().toArray(),
	isLoadingPrices: ( state.fetchPricingPlansRequest as Record<string, unknown> )?.sending
} ) )( PricingSubscriptionSelectionModalWithObserver );
